import { Result } from '../models/result.ts'
import { Globals } from '../globals.ts'
import { TaggedLogger, type Logger } from '../logging/logger.ts'
import { Env } from '../env.ts'

/** @internal */
export enum DataStoreName {
    sub = 'sub',
    sdk = 'sdk',
    WORKER = 'w',
}

/** @internal */
export enum StorageType {
    IDB = 'idb',
    COOKIE = 'cookie',
    LEGACY_COOKIE = 'legacy_cookie',
    MEM = 'mem',
    SESSION = 'session',
}

/** @internal */
export type TimestampedDataValue<T = any> = { value: T; ts: number }

/** @internal */
export type DataStoreRecord<T = any> = TimestampedDataValue<T> & { sourceType: StorageType }

/** @internal */
export type DataStoreGetResult<T = any> = Result<DataStoreRecord<T>>

export type SynchronousDataStore = AbstractDataStore & {
    _getDataSync<T>(key: string): DataStoreGetResult<T>
    _setDataSync<T>(key: string, value: T): Result<T>
    _removeDataSync(key: string): Result<null>
}

/** @internal */
export abstract class AbstractDataStore {
    public readonly _storageType: StorageType

    protected readonly _storeName: DataStoreName | null
    protected readonly _logger: Logger

    protected constructor(storageType: StorageType, storeName: DataStoreName | null, logger?: Logger) {
        this._storageType = storageType
        this._storeName = storeName
        this._logger = logger ?? TaggedLogger._getInstance(`${storageType}${storeName ? `:${storeName}` : ''}`)
    }

    protected get _isInWorkerGlobalScope(): boolean {
        return Env._isInWorkerGlobalScope
    }

    /**
     * Extracts the [lastUpdatedAt]{@link _pnStorageTypeLuaKey} key value for the given
     * DataStore + DataStoreName combination.
     */
    public async _getLastUpdatedAt(): Promise<Date> {
        const req = await this._getData(Globals._pnStorageTypeLuaKey)
        if (req.isErr()) {
            return new Date(0)
        }
        return new Date(req.unwrap().ts)
    }

    /**
     * Updates the [lastUpdatedAt]{@link _pnStorageTypeLuaKey} key value for the given
     * DataStore + DataStoreName combination.
     */
    public async _setLastUpdatedAt(lua?: Date | number): Promise<Result<Date>> {
        const ts = new Date(lua!)

        const res = await this._setData(Globals._pnStorageTypeLuaKey, ts.getTime())
        if (res.isErr()) {
            return res
        }

        return Result.Ok(ts)
    }

    public abstract readonly _isAvailable: boolean

    public abstract _getData<T>(key: string): Promise<DataStoreGetResult<T>>
    public abstract _setData<T>(key: string, value: T): Promise<Result<T>>
    public abstract _removeData(key: string): Promise<Result<null>>
}
